// src/util/themeProvider.js

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react"
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"

// Create a context for theme state management
const ThemeContext = createContext()

// Custom hook to access the theme context
export const useTheme = () => useContext(ThemeContext)

// Main theme provider component
export const ThemeProvider = ({ children }) => {
  // Initialize with a default theme (light) for both server and client
  // This ensures consistent initial rendering
  const [isDark, setIsDark] = useState(false)

  // Check if we're in the browser environment (for SSR compatibility)
  const isBrowser = typeof window !== "undefined"

  // Effect to update theme from localStorage after initial render
  // This runs only on the client side after hydration is complete
  useEffect(() => {
    if (isBrowser) {
      const savedMode = localStorage.getItem("theme-mode")
      if (savedMode === "dark") {
        setIsDark(true)
      }
    }
  }, [isBrowser])

  // Toggle between light and dark mode
  const toggleTheme = () => {
    setIsDark(prev => !prev)
  }

  // Save theme preference to localStorage when it changes and update document
  useEffect(() => {
    if (isBrowser) {
      localStorage.setItem("theme-mode", isDark ? "dark" : "light")

      // Apply theme to document body
      const body = document.body
      if (isDark) {
        body.setAttribute("data-theme", "dark")
        body.style.backgroundColor = "#121212"
        body.style.color = "#ffffff"
      } else {
        body.setAttribute("data-theme", "light")
        body.style.backgroundColor = "#fff"
        body.style.color = "rgba(0, 0, 0, 0.87)"
      }
    }
  }, [isDark, isBrowser])

  // Create the theme based on current mode
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: {
            main: "#363FB9", // Deep Indigo - core brand color
            light: "#5259C7", // Lighter indigo for hover states
            dark: "#2a318f", // Darker indigo for pressed states
          },
          secondary: {
            main: "#DAA7BF", // Warm mauve - accent color
            light: "#e8c0d4",
            dark: "#C47891", // Deeper mauve for contrast
          },
          accent: {
            mauve: "#A5698F", // Rich mauve for special highlights
          },
          background: {
            default: isDark ? "#121212" : "#fff",
            paper: isDark ? "#1e1e1e" : "#fff",
          },
          text: {
            primary: isDark ? "#ffffff" : "rgba(0, 0, 0, 0.87)",
            secondary: isDark
              ? "rgba(255, 255, 255, 0.7)"
              : "rgba(0, 0, 0, 0.6)",
          },
          // Custom palette values for specific components
          custom: {
            glassCard: {
              background: isDark
                ? "rgba(30, 30, 30, 0.85)"
                : "rgba(255, 255, 255, 0.85)",
              border: isDark
                ? "1px solid rgba(218, 167, 191, 0.15)"
                : "1px solid rgba(54, 63, 185, 0.2)",
              text: isDark ? "#ffffff" : "rgba(0, 0, 0, 0.87)",
              shadow: isDark
                ? "0 8px 32px rgba(0, 0, 0, 0.35)"
                : "0 8px 32px rgba(54, 63, 185, 0.15)",
            },
            sections: {
              // Primary background - used for most sections
              primary: isDark ? "#121212" : "#ffffff",
              // Subtle alternate for visual rhythm
              alternate: isDark ? "#1a1d3a" : "#f8f9fa",
              // Legacy support (mapped to new system)
              light: isDark ? "#121212" : "#f8f9fa",
              medium: isDark ? "#1a1d3a" : "#e9ecef",
              // Gradient backgrounds for hero/CTA sections
              gradient1: isDark
                ? "radial-gradient(ellipse at bottom center, #A5698F 0%, #363FB9 50%, #2a318f 100%)"
                : "radial-gradient(ellipse at bottom center, #DAA7BF 0%, #5259C7 50%, #363FB9 100%)",
              gradient2: isDark
                ? "linear-gradient(135deg, #2a318f 0%, #363FB9 50%, #5259C7 100%)"
                : "linear-gradient(135deg, #f5ebf1 0%, #e8ecf8 50%, #dce0f5 100%)",
              // Hero gradient
              hero: isDark
                ? "linear-gradient(180deg, #1a1d3a 0%, #2a318f 100%)"
                : "linear-gradient(180deg, #f7f8ff 0%, #eef0ff 100%)",
              // CTA gradient
              cta: isDark
                ? "radial-gradient(ellipse at top, #2a318f 0%, #1a1d3a 50%, #0f1127 100%)"
                : "radial-gradient(ellipse at top, #f0f2ff 0%, #faf6f8 50%, #ffffff 100%)",
            },
            accent: {
              mauve: "#A5698F",
              mauveLight: "#DAA7BF",
              mauveMid: "#C47891",
              indigo: "#363FB9",
              indigoLight: "#5259C7",
            },
          },
        },
        components: {
          // Card styling from the original static theme
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundColor: isDark ? "#2d2d2d" : "#363FB9",
                color: "#fff",
                borderRadius: "12px",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: isDark
                    ? "0 12px 28px rgba(165, 105, 143, 0.3)"
                    : "0 12px 28px rgba(54, 63, 185, 0.25)",
                },
              },
            },
          },
          MuiAccordion: {
            styleOverrides: {
              root: {
                backgroundColor: isDark
                  ? "rgba(30, 30, 30, 0.85)"
                  : "rgba(255, 255, 255, 0.85)", // Increased opacity for better readability
                color: isDark ? "#ffffff" : "rgba(0, 0, 0, 0.87)", // Darker text for better contrast in light mode
                borderRadius: "8px",
                marginBottom: "8px",
                "&:before": {
                  display: "none", // Remove the default divider
                },
                boxShadow: isDark
                  ? "0 4px 12px rgba(0, 0, 0, 0.2)"
                  : "0 4px 12px rgba(63, 81, 181, 0.1)",
              },
            },
          },
          MuiAccordionSummary: {
            styleOverrides: {
              root: {
                "& .MuiSvgIcon-root": {
                  color: isDark ? "#DAA7BF" : "#363FB9",
                  transition: "transform 0.3s ease",
                },
                "&.Mui-expanded .MuiSvgIcon-root": {
                  transform: "rotate(180deg)",
                },
                padding: "12px 16px",
              },
              content: {
                margin: "8px 0",
              },
            },
          },
          MuiAccordionDetails: {
            styleOverrides: {
              root: {
                padding: "8px 16px 16px",
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                transition: "all 0.3s ease",
                boxShadow: "none",
              },
              containedPrimary: {
                backgroundColor: "#363FB9",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor: "#5259C7",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 12px rgba(54, 63, 185, 0.3)",
                },
              },
              outlinedPrimary: {
                color: isDark ? "#ffffff" : "#363FB9",
                borderColor: isDark ? "#DAA7BF" : "#363FB9",
                borderWidth: "2px",
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(218, 167, 191, 0.08)"
                    : "rgba(54, 63, 185, 0.08)",
                  borderColor: isDark ? "#C47891" : "#5259C7",
                  transform: "translateY(-2px)",
                },
              },
              textPrimary: {
                color: isDark ? "#DAA7BF" : "#363FB9",
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(218, 167, 191, 0.08)"
                    : "rgba(54, 63, 185, 0.08)",
                },
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                color: "inherit",
                transition: "transform 0.3s ease, background-color 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1)",
                  backgroundColor: isDark
                    ? "rgba(218, 167, 191, 0.08)"
                    : "rgba(54, 63, 185, 0.08)",
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
                borderRadius: "12px",
              },
              elevation1: {
                boxShadow: isDark
                  ? "0 4px 20px rgba(0, 0, 0, 0.2)"
                  : "0 4px 20px rgba(0, 0, 0, 0.08)",
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              colorDefault: {
                backgroundColor: isDark
                  ? "rgba(30, 30, 30, 0.9)"
                  : "rgba(255, 255, 255, 0.9)",
                color: isDark ? "#ffffff" : "inherit",
                backdropFilter: "blur(10px)",
                boxShadow: isDark
                  ? "0 4px 20px rgba(0, 0, 0, 0.2)"
                  : "0 4px 20px rgba(0, 0, 0, 0.05)",
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: "8px",
                fontWeight: 500,
              },
              colorPrimary: {
                backgroundColor: isDark
                  ? "rgba(54, 63, 185, 0.2)"
                  : "rgba(54, 63, 185, 0.1)",
                color: isDark ? "#DAA7BF" : "#363FB9",
                border: isDark ? "1px solid rgba(218, 167, 191, 0.3)" : "none",
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                borderColor: isDark
                  ? "rgba(255, 255, 255, 0.12)"
                  : "rgba(0, 0, 0, 0.08)",
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                backgroundColor: isDark ? "#424242" : "#616161",
                borderRadius: "6px",
                fontSize: "0.75rem",
                padding: "8px 12px",
              },
            },
          },
        },
        typography: {
          fontFamily: [
            "Inter",
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            '"Helvetica Neue"',
            "Arial",
            "sans-serif",
          ].join(","),
          h1: {
            fontWeight: 700,
            letterSpacing: "-0.01em",
          },
          h2: {
            fontWeight: 700,
            letterSpacing: "-0.01em",
          },
          h3: {
            fontWeight: 600,
          },
          h4: {
            fontWeight: 600,
          },
          h5: {
            fontWeight: 500,
          },
          h6: {
            fontWeight: 500,
          },
          subtitle1: {
            fontWeight: 500,
          },
          button: {
            fontWeight: 600,
            letterSpacing: "0.02em",
          },
        },
      }),
    [isDark]
  )

  // Provide both the theme and the toggle function to children
  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}

export default ThemeProvider
